import React from 'react';
import { Button, Form, Grid, Input, NumberInput, Slider, Typography } from '@thimble/core-lib';
import '@thimble/core-lib/src/_theme/common.scss';
import '@thimble/core-lib/src/_theme/Grid.scss';
import '@thimble/core-lib/src/_theme/Typography.scss';
import '@thimble/core-lib/src/_theme/Button.scss';
import '@thimble/core-lib/src/_theme/Form.scss';
import '@thimble/core-lib/src/_theme/Input.scss';
import '@thimble/core-lib/src/_theme/NumberInput.scss';
import '@thimble/core-lib/src/_theme/Slider.scss';
import '@thimble/core-lib/src/_theme/Popper.scss';
import statejson from './utils/states';
import useDebounce from './hooks/useDebounce';
import './styles.scss';
import ProfessionControl from './components/ProfessionControl';
import Tooltip from './atoms/Tooltip';
import { ActivityAnswer } from './apis/profession';
import useRequest from './hooks/useRequest';
import { apiQuote } from './apis/quote';
import ZipcodeAndStateControl from './components/ZipcodeAndStateControl';
import { apiCoverageTypes } from './apis/zipcode';
import FieldSet from './atoms/FieldSet';
import Spin from './atoms/Spin';
import { brokerId, partnerId } from './utils/locationSearchParams';
import ICInput from './atoms/ICInput';
import ICNumberInput from './atoms/ICNumberInput';
import { delegateLocationAttribute } from './utils/attribution';

const { Row, Col } = Grid;
const { H3, P, Text } = Typography;

const buildPlanList = ({
  model,
  equipment_limit,
  coverage_limit,
}: {
  model: any;
  equipment_limit: number;
  coverage_limit: number;
}): any[] => {
  const availablePlanList = model.plan_list.filter((plan: any) => plan.is_display);

  const gl = availablePlanList.find(({ insurance_type }: any) => insurance_type === 'gl');
  const pl = availablePlanList.find(({ insurance_type }: any) => insurance_type === 'pl');
  const im = availablePlanList.find(({ insurance_type }: any) => insurance_type === 'im');

  const calcCoverageValue = (userInputLimit: any, plan: any) => {
    const limitPreference = plan.preference.options.find(({ type }: any) => type === 'limit');
    const limitOptions = limitPreference.options.map(({ value }: any) => value);
    return limitOptions.find((i: any) => i >= userInputLimit) || limitOptions.slice(-1)[0];
  };

  if (coverage_limit > 0) {
    // NOTE: 正常流程这些数据在 PlanSelectList 里就已构建好
    const selectedGL = {
      id: gl.id,
      is_selected: true,
      insurance_type: 'gl',
      user_preference: {
        options: [
          {
            type: 'limit',
            select_value: calcCoverageValue(coverage_limit, gl),
          },
        ],
      },
    };
    const selectedPl = pl && {
      id: pl.id,
      is_selected: true,
      insurance_type: 'pl',
      user_preference: {
        options: [
          {
            type: 'limit',
            select_value: calcCoverageValue(coverage_limit, gl),
          },
        ],
      },
    };
    const selectedIM = equipment_limit > 0 &&
      im && {
        id: im.id,
        is_selected: true,
        insurance_type: 'im',
        user_preference: {
          options: [
            {
              type: 'limit',
              select_value: calcCoverageValue(equipment_limit, im),
            },
          ],
        },
      };
    return [selectedGL, selectedPl, selectedIM].filter(Boolean);
  }
  return [];
};

type InsuranceCalculatorFormValue = {
  crew: string;
  profession: ActivityAnswer | null;
  zipcodeAndState: [string, string] | null;
  coverage_limit: number;
  equipment_limit: string;
};

type SafeInsuranceCalculatorFormValue = {
  crew: number;
  profession: ActivityAnswer;
  zipcodeAndState: [string, string];
  coverage_limit: number;
  equipment_limit: number;
};

const InsuranceCalculator: React.FC = () => {
  const [{ value: quote, loading: quoteLoading }, triggerQuote, clearQuote] = useRequest(
    async ({
      crew,
      profession,
      zipcodeAndState,
      coverage_limit,
      equipment_limit,
    }: SafeInsuranceCalculatorFormValue) => {
      const model = await apiQuote({
        crew,
        profession,
        zipcodeAndState,
      });

      const plan_list = await buildPlanList({ model, coverage_limit, equipment_limit });

      return apiQuote({
        crew,
        profession,
        zipcodeAndState,
        plan_list,
      });
    }
  );
  // https://app-test.thimble.com/pre-quote?quoteid=c9d58d16-035f-4a09-8cd0-350df30ef770
  const handleRedirectToWebApp = () => {
    const utm = brokerId
      ? `utm_source=webapp&utm_medium=insurance-calculator-widget&utm_campaign=broker:${brokerId}`
      : delegateLocationAttribute();

    if (quote?.quote_detail && typeof quote.quote_detail !== 'string') {
      window.open(
        `${process.env.RAZZLE_WEBAPP_DOMAIN}/pre-quote?quoteid=${quote.quote_id}${
          utm ? `&${utm}` : ''
        }`
      );
    } else {
      window.open(`${process.env.RAZZLE_WEBAPP_DOMAIN}${utm ? `?${utm}` : ''}`);
    }
  };

  const formRef = Form.useRef<InsuranceCalculatorFormValue>();

  const recommendedCoverageText = quote?.quote_detail.rate_factor?.pl
    ? 'General Liability + Professional Liability'
    : 'General Liability';
  const recommendedCoverageValue =
    Math.round(
      (quote?.quote_detail.rate_factor?.gl?.estimate_annual_rate || 0) / 12 +
        (quote?.quote_detail.rate_factor?.pl?.estimate_annual_rate || 0) / 12
    ) || '--';
  const recommendedIMCoverageValue =
    Math.round((quote?.quote_detail.rate_factor?.im?.estimate_annual_rate || 0) / 12) || '--';

  const [autoSubmitForm] = useDebounce(() => {
    if (!formRef.current) return;

    const value = formRef.current.value;
    if (value.profession && value.zipcodeAndState) {
      formRef.current.submit();
    }
  }, 500);

  const handleSubmit = (value: InsuranceCalculatorFormValue) => {
    // ts
    if (!value.profession) return;
    if (!value.zipcodeAndState) return;

    // cannot select zero coverage limit
    if (value.coverage_limit === 0) {
      clearQuote();
      return;
    }

    const crew = Number(value.crew || 0);
    const equipment_limit = Number(value.equipment_limit || 0);
    const coverage_limit = value.coverage_limit;
    triggerQuote({
      coverage_limit,
      crew,
      equipment_limit,
      profession: value.profession,
      zipcodeAndState: value.zipcodeAndState,
    });
  };

  return (
    <div className="insurance-calculator-widget">
      <div style={{ textAlign: 'center' }}>
        <Col align="center" className="mb-12">
          <a target="_blank" href="https://www.thimble.com/" rel="noreferrer">
            <img
              src={`${process.env.RAZZLE_ASSETS_DOMAIN}/assets/images/thimble-logo-title.png`}
              className="thimble-logo-title"
              alt="Thimble Logo"
            />
          </a>
          <H3 style={{ marginBottom: 0 }}>Business Insurance Calculator</H3>
          <P preset="body" color="black" style={{ maxWidth: 320 }}>
            Answer a few short questions to find out what insurance your business needs.
          </P>
        </Col>
      </div>
      <Form<InsuranceCalculatorFormValue>
        ref={formRef}
        initialValue={{
          crew: '',
          profession: null,
          zipcodeAndState: null,
          equipment_limit: '',
          coverage_limit: 1000000,
        }}
        action={handleSubmit}
        onUpdate={() => {
          autoSubmitForm();
        }}
      >
        <FieldSet>
          <Form.Item<string>
            name="crew"
            label="Number of Employees"
            rules={[
              {
                validator(_rules, value: string) {
                  if (!value) return true;
                  const result = Number.isInteger(Number(value)) && /^[0-9]{0,8}$/.test(value);
                  if (!result) {
                    return new Error('Not a valid number');
                  }
                  return true;
                },
              },
            ]}
          >
            <ICInput
              label={[
                'Number of Employees',
                <Tooltip key="1">
                  {`Include owners (that's you!), full-time, part-time and temporary employees.`}
                </Tooltip>,
              ]}
            />
          </Form.Item>
          <Form.Item<ActivityAnswer | null>
            name="profession"
            label="Profession"
            rules={[{ required: true }]}
          >
            <ProfessionControl />
          </Form.Item>
        </FieldSet>
        <FieldSet>
          <Form.Item<[string, string] | null>
            name="zipcodeAndState"
            label="ZIP Code"
            rules={[
              {
                async asyncValidator(_rules, value: [string, string] | null) {
                  if (!value?.[0]) {
                    return Promise.reject('ZIP Code is required');
                  } else if (!value?.[1]) {
                    return Promise.reject('invalid ZIP Code');
                  } else {
                    const isZipcodeCovered = await apiCoverageTypes(...value);
                    if (isZipcodeCovered) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      `unfortunately, Thimble isn’t available yet in ${
                        (statejson as any)[value[1]]
                      }`
                    );
                  }
                },
              },
            ]}
          >
            <ZipcodeAndStateControl />
          </Form.Item>
          <Form.Item
            name="equipment_limit"
            label="Value of Your Equipment"
            rules={[
              {
                validator(_rules, value: string) {
                  if (!value) return true;
                  const result = /^[0-9.]{0,10}$/.test(value);
                  if (!result) {
                    return new Error('Not a valid number');
                  }
                  return true;
                },
              },
            ]}
          >
            <ICNumberInput
              label={[
                'Value of Your Equipment',
                <Tooltip key="1">
                  The total worth of all the equipment you’ll need on the job to get things done. It
                  doesn’t need to be exact—just a best guess.
                </Tooltip>,
              ]}
              placeholder="$2,500"
              formatter={{
                prefix: '$',
                thousandSeparator: true,
              }}
              onValueChange={console.log}
            />
          </Form.Item>
        </FieldSet>
        <Text preset="body" color="black">
          Coverage Limit
        </Text>
        <div style={{ paddingLeft: 20, paddingRight: 20 }}>
          <Form.Item name="coverage_limit" label="Coverage Limit">
            <Slider
              min={0}
              max={2000000}
              step={null}
              label={[
                {
                  value: 0,
                  label: '$0M',
                },
                {
                  value: 1000000,
                  label: '$1M',
                },
                {
                  value: 2000000,
                  label: '$2M',
                },
              ]}
              renderLabel={({ exact, label }) => (
                <Text preset="subhead" color={exact ? 'black' : 'dark-grey'}>
                  {label}
                </Text>
              )}
            />
          </Form.Item>
        </div>
        <div style={{ display: 'none' }}>
          <Form.SubmitButton>submit</Form.SubmitButton>
        </div>
      </Form>

      <div
        className="insurance-calculator-widget__result"
        style={{
          opacity: quoteLoading ? 0.5 : undefined,
        }}
      >
        <div>
          <Row wrap>
            <Col>
              <div>
                <Text preset="subhead" color="dark-green">
                  Recommended coverage
                  <Tooltip>
                    This is the coverage we recommend so you can get to work, but you can always
                    customize your limits as needed.
                  </Tooltip>
                </Text>
              </div>
              <div>
                <Text preset="body">{recommendedCoverageText}</Text>
              </div>
            </Col>
            <Col className="ml-auto insurance-calculator-widget__price">
              <Row align="center">
                <Col className="mr-8">
                  <Text preset="h2">${recommendedCoverageValue}</Text>
                </Col>
                <Col>
                  <Text preset="body">Avg / Month</Text>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
        <div>
          <Row wrap>
            <Col>
              <Text preset="body" color="dark-green">
                Business Equipment Protection
                <Tooltip>
                  This covers you for damage to all the stuff you own or use for work.
                </Tooltip>
              </Text>
            </Col>
            <Col className="ml-auto insurance-calculator-widget__price">
              <Text preset="body">${recommendedIMCoverageValue} Avg / Month</Text>
            </Col>
          </Row>
        </div>
        <Col align="center" className="mt-24">
          <Button.Primary onClick={handleRedirectToWebApp}>Get Covered</Button.Primary>
          <Row className="mt-12">
            <P
              preset="legal"
              style={{
                maxWidth: 400,
                textAlign: 'center',
                transform: 'scale(0.9)',
                transformOrigin: 'bottom',
              }}
            >
              Prices shown are for illustrative purposes only and are a non-binding indication of
              premium. Final prices may vary.
            </P>
          </Row>
        </Col>
        {quoteLoading ? (
          <div className="insurance-calculator-widget__result__loading">
            <Spin />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default InsuranceCalculator;
